import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/i18n";

const CONTENT = {
  en: {
    links: [
      { to: "/", id: "home", label: "Home" },
      { to: "/about", id: "about", label: "About" },
      { to: "/services", id: "services", label: "Services" },
      { to: "/articles", id: "articles", label: "Articles" },
      { to: "/faq", id: "faq", label: "FAQ" },
    ],
    cta: "Book a Consultation",
    ctaShort: "Book now",
    home: "Ioannis Tsiros Psychotherapy home",
  },
  el: {
    links: [
      { to: "/", id: "home", label: "Αρχική" },
      { to: "/about", id: "about", label: "Για εμένα" },
      { to: "/services", id: "services", label: "Υπηρεσίες" },
      { to: "/articles", id: "articles", label: "Άρθρα" },
      { to: "/faq", id: "faq", label: "Συχνές ερωτήσεις" },
    ],
    cta: "Κλείστε Ραντεβού",
    ctaShort: "Ραντεβού",
    home: "Ioannis Tsiros Psychotherapy home",
  },
};

export const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-0.5 rounded-full border border-sage-600/20 p-1 ${className}`} data-testid="lang-toggle">
      {["en", "el"].map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLang(l)}
          data-testid={`lang-option-${l}`}
          aria-pressed={lang === l}
          className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide transition-colors duration-200 ${
            lang === l ? "bg-sage-600 text-background" : "text-muted-foreground hover:text-sage-700"
          }`}
        >
          {l === "el" ? "ΕΛ" : "EN"}
        </button>
      ))}
    </div>
  );
};

const Nav = () => {
  const { lang } = useLang();
  const c = CONTENT[lang];
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-sage-900/25 backdrop-blur-[2px] md:hidden"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-backdrop"
          />
        )}
      </AnimatePresence>
      <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-300 ${
        scrolled || open
          ? "bg-background/85 shadow-[0_1px_0_rgba(58,77,66,0.08)] backdrop-blur-xl"
          : "bg-background/60 backdrop-blur-sm"
      }`}
    >
      <div className="container-x flex h-20 items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" aria-label={c.home}>
          <Logo />
        </Link>

        <nav className="hidden items-center gap-9 md:flex">
          {c.links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `link-underline text-sm tracking-wide transition-colors duration-200 ${
                  isActive ? "text-sage-700" : "text-muted-foreground hover:text-sage-700"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <LangToggle />
          <Button
            asChild
            data-testid="nav-cta-book"
            className="rounded-full bg-sage-600 px-6 text-background hover:bg-sage-700"
          >
            <Link to="/intake">{c.cta}</Link>
          </Button>
        </nav>

        <div className="flex items-center gap-2 md:hidden">
          <Button
            asChild
            data-testid="nav-mobile-header-cta"
            className="rounded-full bg-sage-600 px-5 text-background hover:bg-sage-700"
          >
            <Link to="/intake">{c.ctaShort}</Link>
          </Button>
          <button
            className="rounded-full p-2 text-sage-800"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-sage-600/10 bg-background/95 backdrop-blur-xl md:hidden"
          >
            <div className="container-x flex flex-col gap-1 py-6">
              {c.links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-3 font-display text-2xl ${
                      isActive ? "text-sage-700" : "text-foreground"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="px-4 pt-3">
                <LangToggle />
              </div>
              <Button
                asChild
                data-testid="nav-mobile-cta-book"
                className="mt-4 rounded-full bg-sage-600 px-6 text-background hover:bg-sage-700"
              >
                <Link to="/intake">{c.cta}</Link>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
    </>
  );
};

export default Nav;
